import React from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import CategorySelect from "../CategorySelect";
import { MainCategory } from "../../data/categories";
import { GearPal } from "../../types/Pal";
import {
  IoAdd,
  IoLocationOutline,
  IoCallOutline,
  IoMusicalNotes,
  IoTrashOutline,
} from "react-icons/io5";
import GearButton from "../../ui/gearbutton";
import GearInput from "../../ui/gearinput";

type GearPalItemForm = {
  name: string;
  categoryId: string;
  externalLink?: string;
  categoryLabel?: string;
};

interface GearPalFormProps {
  registerGearPal: any;
  gearPalErrors: any;
  gearPalTouchedFields: any;
  isGearPalSubmitted: boolean;
  watchGearPal: any;
  setGearPalValue: any;
  triggerGearPal: any;
  isGearPalValid: boolean;
  isAddingGearPal: boolean;
  isUpdatingGearPal?: boolean;
  isEdit?: boolean;
  palToEdit?: GearPal;
}

const GearPalForm: React.FC<GearPalFormProps> = ({
  registerGearPal,
  gearPalErrors,
  gearPalTouchedFields,
  isGearPalSubmitted,
  watchGearPal,
  setGearPalValue,
  triggerGearPal,
  isGearPalValid,
  isAddingGearPal,
  isUpdatingGearPal = false,
  isEdit = false,
  palToEdit = null,
}) => {
  const { t } = useTranslation();
  const items = (watchGearPal("items") as GearPalItemForm[]) || [];
  const contacts = watchGearPal("contacts") || [];

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "rgb(51, 24, 85)",
      borderColor: state.isFocused ? "rgba(90, 24, 196, 0.6)" : "#6e11b04d",
      borderRadius: "0.5rem",
      fontSize: "14px",
      minHeight: "40px",
      color: "white",
      boxShadow: "none",
      "&:hover": {
        borderColor: "rgba(90, 24, 196, 0.6)",
      },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1F1235",
      borderRadius: "0.75rem",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      zIndex: 100,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#5A18C4"
        : state.isFocused
        ? "rgba(90, 24, 196, 0.2)"
        : "transparent",
      color: "white",
      cursor: "pointer",
      "&:active": {
        backgroundColor: "#5A18C4",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#6a7282 !important",
      fontSize: "14px !important",
    }),
    input: (base: any) => ({
      ...base,
      color: "white",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "rgba(255, 255, 255, 0.4)",
      "&:hover": {
        color: "white",
      },
    }),
  };

  return (
    <div className="space-y-8">
      {/* Informazioni Generali */}
      <div className="space-y-4">
        <GearInput
          {...registerGearPal("name")}
          label={t("forms.name")}
          placeholder={t("forms.name")}
          error={gearPalErrors.name?.message}
        />

        <div className="p-5 rounded-2xl border border-[#451671] bg-[#331855]/30 space-y-4">
          <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
            <IoLocationOutline size={20} className="text-primary" />{" "}
            {t("forms.location", "Località")}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <GearInput
              {...registerGearPal("city")}
              label={t("forms.city", "Città")}
              placeholder="Roma"
              error={gearPalErrors.city?.message}
            />
            <GearInput
              {...registerGearPal("zipcode")}
              label={t("forms.zipCode", "CAP")}
              placeholder="12345"
              error={gearPalErrors.zipcode?.message}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <GearInput
              {...registerGearPal("region")}
              label={t("forms.region", "Regione")}
              placeholder="Lazio"
              error={gearPalErrors.region?.message}
            />
            <GearInput
              {...registerGearPal("country")}
              label={t("forms.country", "Paese")}
              placeholder="Italy"
              error={gearPalErrors.country?.message}
            />
          </div>
          <p className="mt-1 text-xs text-secondary/70 italic lowercase">
            {t(
              "forms.zipCodeHint",
              "Per motivi di sicurezza, mostriamo solo la zona del codice postale sulla mappa"
            )}
          </p>
        </div>
      </div>

      {/* Contatti */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#331855]/30 space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoCallOutline size={20} className="text-primary" />{" "}
          {t("forms.contacts", "Contatti")}
        </h4>
        <div className="space-y-4">
          <p className="text-xs text-secondary/70">
            {t(
              "forms.contactHint",
              "Aggiungi i tuoi metodi di contatto preferiti. Almeno un contatto è obbligatorio."
            )}
          </p>
          {contacts.map((contact: any, index: number) => (
            <div
              key={index}
              className="flex gap-2 items-start bg-[#1F1235]/50 p-3 rounded-lg border border-white/5"
            >
              <div className="flex-grow space-y-3">
                <div className="isMultiSelect">
                  <Select
                    styles={selectStyles}
                    options={[
                      { value: "email", label: "EMAIL" },
                      { value: "instagram", label: "INSTAGRAM" },
                      { value: "x", label: "X" },
                      { value: "linkedin", label: "LINKEDIN" },
                      { value: "facebook", label: "FACEBOOK" },
                      { value: "website", label: "WEBSITE" },
                      { value: "telegram", label: "TELEGRAM" },
                    ]}
                    value={[
                      { value: "email", label: "EMAIL" },
                      { value: "instagram", label: "INSTAGRAM" },
                      { value: "x", label: "X" },
                      { value: "linkedin", label: "LINKEDIN" },
                      { value: "facebook", label: "FACEBOOK" },
                      { value: "website", label: "WEBSITE" },
                      { value: "telegram", label: "TELEGRAM" },
                    ].find((o) => o.value === contact.type)}
                    onChange={(val: any) => {
                      const newContacts = [...contacts];
                      newContacts[index] = { ...contact, type: val.value };
                      setGearPalValue("contacts", newContacts);
                    }}
                    isSearchable={false}
                  />
                </div>
                <GearInput
                  value={contact.value}
                  onChange={(e) => {
                    const newContacts = [...contacts];
                    newContacts[index] = { ...contact, value: e.target.value };
                    setGearPalValue("contacts", newContacts);
                  }}
                  placeholder={t("forms.value", "Valore")}
                  containerClassName="mb-0"
                  error={gearPalErrors.contacts?.[index]?.value?.message}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const newContacts = contacts.filter(
                    (_: any, i: number) => i !== index
                  );
                  setGearPalValue("contacts", newContacts);
                }}
                className="mt-2.5 p-2 bg-red-500/10 text-red-500 rounded-full hover:bg-red-500/20 transition-colors"
              >
                <IoTrashOutline size={18} />
              </button>
            </div>
          ))}
          <GearButton
            type="button"
            variant="ghost"
            onClick={() => {
              const newContacts = [...contacts, { type: "email", value: "" }];
              setGearPalValue("contacts", newContacts);
            }}
            className="w-full !border-dashed !border-white/20 !bg-transparent hover:!bg-white/5"
          >
            <div className="flex items-center gap-2 justify-center py-1">
              <IoAdd /> {t("forms.addContact", "Aggiungi Contatto")}
            </div>
          </GearButton>
        </div>
      </div>

      {/* Gear Items */}
      <div className="p-5 rounded-2xl border border-[#451671] bg-[#331855]/30 space-y-4">
        <h4 className="text-secondary font-semibold flex items-center gap-2 mb-2">
          <IoMusicalNotes size={20} className="text-primary" />{" "}
          {t("forms.items", "Articoli")}
        </h4>
        <div className="space-y-6">
          {items.map((item: GearPalItemForm, index: number) => (
            <div
              key={index}
              className="p-4 rounded-xl border border-white/5 bg-[#1F1235]/30 relative space-y-4"
            >
              <button
                type="button"
                onClick={() => {
                  const newItems = items.filter(
                    (_: any, i: number) => i !== index
                  );
                  setGearPalValue("items", newItems);
                }}
                className="absolute -top-2 -right-2 w-7 h-7 bg-[#2D1B4E] border border-white/10 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors shadow-lg"
              >
                ✕
              </button>

              <CategorySelect
                value={
                  item.categoryLabel
                    ? {
                        main: item.categoryLabel.split(
                          " > "
                        )[0] as MainCategory,
                        sub: item.categoryLabel.split(" > ")[1],
                        type: item.categoryLabel.split(" > ")[2],
                      }
                    : null
                }
                onChange={(value) => {
                  const newItems = [...items];
                  newItems[index] = {
                    ...item,
                    categoryId: value ? value.type : "",
                    categoryLabel: value
                      ? `${value.main} > ${value.sub} > ${value.type}`
                      : "",
                  };
                  setGearPalValue("items", newItems);
                }}
                useTranslationKeys={true}
                useBackendCategories={true}
                className="bg-[#331855]"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <GearInput
                  value={item.name}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[index] = { ...item, name: e.target.value };
                    setGearPalValue("items", newItems);
                  }}
                  label={t("forms.itemName", "Nome Articolo")}
                  placeholder={t("forms.itemName")}
                  containerClassName="mb-0"
                  error={gearPalErrors.items?.[index]?.name?.message}
                />
                <GearInput
                  type="url"
                  value={item.externalLink}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[index] = { ...item, externalLink: e.target.value };
                    setGearPalValue("items", newItems);
                  }}
                  label={t("forms.externalLink", "Link Esterno")}
                  placeholder="https://..."
                  containerClassName="mb-0"
                  error={gearPalErrors.items?.[index]?.externalLink?.message}
                />
              </div>
            </div>
          ))}

          <GearButton
            type="button"
            variant="ghost"
            onClick={() => {
              setGearPalValue("items", [
                ...items,
                {
                  categoryId: "",
                  name: "",
                  externalLink: "",
                  categoryLabel: "",
                },
              ]);
            }}
            className="w-full !border-dashed !border-white/20 !bg-transparent hover:!bg-white/5"
          >
            <div className="flex items-center gap-2 justify-center py-1">
              <IoAdd /> {t("forms.addItem", "Aggiungi Articolo")}
            </div>
          </GearButton>
        </div>
      </div>

      {/* Validations Error Summary */}
      {!isGearPalValid && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <h4 className="text-sm font-semibold text-red-500 mb-2">
            {t(
              "forms.validationErrors",
              "Per favore correggi i seguenti errori:"
            )}
          </h4>
          <ul className="text-xs text-red-400 space-y-1 list-disc pl-4">
            {gearPalErrors.name && <li>{gearPalErrors.name.message}</li>}
            {gearPalErrors.city && <li>{gearPalErrors.city.message}</li>}
            {gearPalErrors.zipcode && <li>{gearPalErrors.zipcode.message}</li>}
            {gearPalErrors.contacts && gearPalErrors.contacts.message && (
              <li>{gearPalErrors.contacts.message}</li>
            )}
            {gearPalErrors.items && gearPalErrors.items.message && (
              <li>{gearPalErrors.items.message}</li>
            )}
          </ul>
        </div>
      )}

      {/* Submit Buttons */}
      <div className="flex justify-end pt-4">
        {!isEdit ? (
          <GearButton
            type="submit"
            variant="secondary"
            disabled={!isGearPalValid || isAddingGearPal}
            className="w-full !rounded-full py-4 shadow-[0_0_20px_rgba(90,24,196,0.3)] hover:shadow-[0_0_30px_rgba(90,24,196,0.5)] transition-all"
          >
            <div className="flex items-center gap-2 justify-center">
              <IoAdd size={22} />
              <span className="text-[14px]">
                {isAddingGearPal
                  ? t("forms.submitting", "Invio in corso...")
                  : t("forms.submit", "Invia")}
              </span>
            </div>
          </GearButton>
        ) : (
          <GearButton
            type="submit"
            variant="secondary"
            disabled={!isGearPalValid || isUpdatingGearPal}
            className="w-full !rounded-full shadow-[0_0_20px_rgba(90,24,196,0.3)] hover:shadow-[0_0_30px_rgba(90,24,196,0.5)] transition-all"
          >
            <div className="flex items-center gap-2 justify-center">
              <span className="text-[14px]">
                {isUpdatingGearPal
                  ? t("forms.updating", "Aggiornamento in corso...")
                  : t("forms.saveChanges", "Salva Modifiche")}
              </span>
            </div>
          </GearButton>
        )}
      </div>
    </div>
  );
};

export default GearPalForm;
